import React, { useState } from 'react';
import { LuArrowLeft, LuCircleCheck, LuCircleX, LuLoaderPinwheel } from 'react-icons/lu';
import { Link, useParams } from 'react-router-dom';

import { TARGET_STATUS_OPTIONS, TRANSFER_PROVIDER_LABEL, TransferStatusMappingEntry } from '../../entities/transfer';
import { confirmTransferJob, updateTransferMapping } from '../../shared/api/transfer';
import { useTransferJob } from '../../shared/hooks/use-transfer-job';

const STATUS_LABEL: Record<string, string> = {
  reading: 'Reading source project…',
  mapping_proposed: 'Review the status mapping',
  mapping_confirmed: 'Starting transfer…',
  transferring: 'Transferring…',
  completed: 'Completed',
  failed: 'Failed',
};

const ItemStatusIcon: React.FC<{ status?: 'creating' | 'created' | 'failed' }> = ({ status }) => {
  if (status === 'created') return <LuCircleCheck className="text-success shrink-0" size={16} />;
  if (status === 'failed') return <LuCircleX className="text-error shrink-0" size={16} />;
  if (status === 'creating') return <LuLoaderPinwheel className="animate-spin opacity-70 shrink-0" size={16} />;
  return <span className="w-4 h-4 rounded-full border border-base-300 shrink-0" />;
};

const TransferJobPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const { job, itemProgress, containerProgress, loading, error, retry } = useTransferJob(jobId);

  const [mapping, setMapping] = useState<TransferStatusMappingEntry[] | null>(null);
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const activeMapping = mapping ?? job?.statusMapping ?? [];

  const handleMappingChange = (sourceStatus: string, targetStatus: string) => {
    const base = mapping ?? job?.statusMapping ?? [];
    setMapping(base.map((m) => (m.sourceStatus === sourceStatus ? { ...m, targetStatus } : m)));
  };

  const handleSaveMapping = async () => {
    if (!jobId || !mapping) return;
    setIsSavingMapping(true);
    setActionError(null);
    try {
      await updateTransferMapping(jobId, mapping);
      setMapping(null); // job update arrives via WS/refetch
    } catch {
      setActionError('Failed to save the mapping.');
    } finally {
      setIsSavingMapping(false);
    }
  };

  const handleConfirm = async () => {
    if (!jobId) return;
    setIsConfirming(true);
    setActionError(null);
    try {
      // Save any unsaved edits first so confirm doesn't run against a stale mapping.
      if (mapping) await updateTransferMapping(jobId, mapping);
      await confirmTransferJob(jobId);
      setMapping(null);
    } catch {
      setActionError('Failed to confirm the transfer.');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    setActionError(null);
    try {
      await retry();
    } catch {
      setActionError('Failed to retry.');
    } finally {
      setIsRetrying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg opacity-40" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="alert alert-error max-w-sm">
          <span>{error ?? 'Transfer job not found.'}</span>
        </div>
      </div>
    );
  }

  const targetOptions = TARGET_STATUS_OPTIONS[job.targetProvider];
  const canEditMapping = job.status === 'mapping_proposed';
  const canRetry = job.status === 'failed' || (job.status === 'completed' && !!job.transferError);

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Link to="/transfer" className="btn btn-ghost btn-sm btn-circle">
            <LuArrowLeft size={16} />
          </Link>
          <div>
            <div className="text-xl font-semibold">
              {TRANSFER_PROVIDER_LABEL[job.sourceProvider]} → {TRANSFER_PROVIDER_LABEL[job.targetProvider]}
            </div>
            <div className="text-sm opacity-70">
              {job.sourceProjectName ?? job.sourceProjectId} → {job.targetProjectName}
            </div>
          </div>
          <span className="badge badge-outline ml-auto">{STATUS_LABEL[job.status] ?? job.status}</span>
        </div>

        {actionError && (
          <div className="alert alert-error">
            <span>{actionError}</span>
          </div>
        )}

        {job.status === 'reading' && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-6 items-center text-center gap-2">
              <span className="loading loading-spinner loading-md" />
              <div className="text-sm opacity-70">Reading the source project and proposing a status mapping…</div>
            </div>
          </div>
        )}

        {job.status === 'failed' && job.readError && (
          <div className="alert alert-error">
            <span>{job.readError}</span>
          </div>
        )}

        {job.statusMapping.length > 0 && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 gap-3">
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">Status mapping</div>
                {canEditMapping && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveMapping}
                    disabled={isSavingMapping || !mapping}
                  >
                    {isSavingMapping && <LuLoaderPinwheel className="animate-spin" size={14} />}
                    Save mapping
                  </button>
                )}
              </div>

              {job.mappingError && (
                <div className="alert alert-warning text-sm py-2">
                  <span>
                    The AI-proposed mapping failed, so a keyword-based fallback was used instead — review it carefully
                    before confirming.
                  </span>
                </div>
              )}

              <div className="space-y-2">
                {activeMapping.map((m) => (
                  <div key={m.sourceStatus} className="flex items-center gap-3">
                    <span className="badge badge-outline flex-1 justify-start truncate">{m.sourceStatus}</span>
                    <span className="opacity-50">→</span>
                    {targetOptions ? (
                      <select
                        className="select select-bordered select-sm flex-1"
                        value={m.targetStatus}
                        disabled={!canEditMapping}
                        onChange={(e) => handleMappingChange(m.sourceStatus, e.target.value)}
                      >
                        {targetOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="input input-bordered input-sm flex-1"
                        value={m.targetStatus}
                        disabled={!canEditMapping}
                        onChange={(e) => handleMappingChange(m.sourceStatus, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>

              {canEditMapping && (
                <div className="flex justify-end pt-2">
                  <button className="btn btn-primary" onClick={handleConfirm} disabled={isConfirming}>
                    {isConfirming && <LuLoaderPinwheel className="animate-spin" size={14} />}
                    Confirm & start transfer
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {containerProgress && job.status === 'transferring' && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 flex-row items-center gap-3">
              {containerProgress.status === 'failed' ? (
                <LuCircleX className="text-error shrink-0" size={18} />
              ) : (
                <LuLoaderPinwheel className="animate-spin shrink-0" size={18} />
              )}
              <div className="text-sm">
                {containerProgress.status === 'failed'
                  ? (containerProgress.error ?? 'Failed to create the target project/board.')
                  : (containerProgress.step ?? 'Creating the target project/board…')}
              </div>
            </div>
          </div>
        )}

        {job.items.length > 0 && job.status !== 'reading' && job.status !== 'mapping_proposed' && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 gap-2">
              <div className="text-lg font-semibold">Items ({job.items.length})</div>
              <div className="space-y-1 max-h-[50vh] overflow-y-auto">
                {job.items.map((item) => {
                  const status = itemProgress[item.id]?.status ?? item.transferStatus;
                  const itemError = itemProgress[item.id]?.error ?? item.transferError;
                  return (
                    <div key={item.id} className="flex items-start gap-2 text-sm bg-base-200 rounded-xl px-3 py-2">
                      <ItemStatusIcon status={status} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{item.title}</span>
                          <span className="badge badge-ghost badge-xs shrink-0">{item.targetStatus}</span>
                        </div>
                        {status === 'failed' && itemError && (
                          <div className="text-xs text-error mt-0.5">{itemError}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {job.status === 'completed' && (
          <div className="alert alert-success">
            <span>
              Transfer complete{job.targetContainerId ? ` — target: ${job.targetContainerId}` : ''}.
              {job.transferError ? ` Note: ${job.transferError}` : ''}
            </span>
          </div>
        )}

        {job.status === 'failed' && !job.readError && (
          <div className="alert alert-error">
            <span>{job.transferError ?? 'The transfer failed.'}</span>
          </div>
        )}

        {canRetry && (
          <button className="btn btn-outline" onClick={handleRetry} disabled={isRetrying}>
            {isRetrying && <LuLoaderPinwheel className="animate-spin" size={14} />}
            Retry failed items
          </button>
        )}
      </div>
    </div>
  );
};

export default TransferJobPage;
